// GOOP ZONE - Main Page
// Flow: Auth → Menu → Game → Leaderboard

import { useState, useCallback, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import TitleScreen from '../components/TitleScreen';
import GameCanvas from '../components/GameCanvas';
import { preloadStaticGameAssets } from '../game/engine';
import MobileGuards from '../components/MobileGuards';
import SettingsScreen from '../components/SettingsScreen';
import QuestsScreen from '../components/QuestsScreen';
import AuthScreen from '../components/AuthScreen';
import IntroSequence from '../components/IntroSequence';
import ModBench from '../components/ModBench';
import DevPanel from '../components/DevPanel';
import ReplayLauncherPanel from '../components/ReplayLauncherPanel';
import PostRunUnlockScreen from '../components/PostRunUnlockScreen';
import WeaponLabScreen from '../components/WeaponLabScreen';
import RoomLabScreen from '../components/RoomLabScreen';
import { isTouchDevice } from '../components/TouchControls';
import type { AudioSettings } from '../game/types';
import { loadUserSettings, saveUserSettings, type UserSettings } from '../game/userSettings';
import {
  applyGraphicsSettings,
  getParticleDensity,
  getResolutionPreset,
  getResolutionScale,
  getScreenShakeEnabled,
  getGlowEffectsEnabled,
  getScreenFlashEnabled,
  getDynamicLightingEnabled,
} from '../game/graphicsSettings';
import { resolveAsset } from '../game/uiAssets';
import { initAudio, startBgm, preloadBgm, resumeAudio, setMasterVolume, setMusicVolume, setSFXVolume, setBgmVolume, preloadDeathEulogy } from '../game/audio';
import { supabase, submitScore, signOut } from '../lib/supabase';
import { loadGoopState, saveGoopState, createGoopSystem, updateLifetimeStats, applyDevUserUnlocks } from '../game/goopSystem';
import type { GoopSystemState, RunResults } from '../game/goopSystem';
import { isDevUser } from '../game/devUsers';  // BETA 15.56 — auto-unlock for dev users
import { getNetworkClass } from '../game/deviceDetect';  // BETA 21.08 — slow-net guard for heavy preloads
import { setUnlockedCharacterIds, activateAllForBotMode, activateAllForDevMode, restoreLoadoutFromStorage } from '../game/loadoutSelection';
// BETA 22.50 — the canvas menu (MenuRenderer/ClassroomMenu) is deleted; the
// live-data feed lives in its own module now.
import { setMenuLiveData, setMenuBotMode } from '../game/menuLiveData';
import { downloadFullCSV, hasBotRunData, getSavedRunCount, clearBotRunData } from '../game/autoPlaytester_v2';

export type Screen = 'auth' | 'menu' | 'game' | 'settings' | 'quests' | 'unlocks' | 'leaderboard' | 'post_run_unlocks' | 'weapon_lab' | 'room_lab' | 'modbench';

// BETA 20.47 — Glue the bottom-left dev buttons to the 16:9 menu box instead of
// the raw viewport. The menu (ClassroomMenu) is a 16:9 letterboxed box centered
// on screen: width = min(100vw, 100dvh*16/9), height = min(100dvh, 100vw*9/16).
// The ● BETA version badge lives INSIDE that box (position:absolute, bottom-left),
// so in non-16:9 aspect ratios the letterbox pushes the badge inward while the
// position:fixed dev buttons stayed pinned to the viewport corner — drifting off
// the menu and covering the badge. These helpers offset a fixed element from the
// box's bottom-left corner instead, so the buttons move in lockstep with the box
// (and the badge) in every aspect ratio. At exactly 16:9 both bars are 0, so the
// positions are identical to before.
// BETA 22.42 — pinned to raw viewport corners.
// BETA 22.49 — Dylan: "why is the dev button not locked in place in the 16:9
// start menu?" Raw-viewport pinning detached the dev buttons from the menu box
// in any non-16:9 aspect ratio: the ● BETA badge lives INSIDE the 16:9 box, so
// it sits at the box's bottom-left while the fixed buttons stayed at the true
// viewport corner — badge mid-screen, DEV way down at the viewport bottom.
// Back to box-relative, but via pure CSS calc that mirrors the box's own sizing
// (width min(100vw, 100dvh*16/9), height min(100dvh, 100vw*9/16)) so it tracks
// the box exactly AND auto-updates on resize. At exactly 16:9 both letterbox
// bars are 0 → positions identical to before (no change on the ROG Ally).
const _stageLeft = (px: number) => `calc((100vw - min(100vw, 100dvh * 16 / 9)) / 2 + ${px}px)`;
const _stageBottom = (px: number) => `calc((100dvh - min(100dvh, 100vw * 9 / 16)) / 2 + ${px}px)`;

// BETA 22.51 — Dylan (video feedback): "hide the dev button so we don't see
// this anymore." The DEV toggle (and the tool stack behind it) only renders
// with ?dev=1 in the URL — invisible on a normal boot, one URL flag away for
// tooling sessions. Bot-farm automation keeps its own params and is unaffected.
const _devToolsRequested = typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('dev');

// Character data matching MenuRenderer's CHARACTERS array
const CHARACTERS = [
  { id: 'tactical',  name: 'Military Guy',         url: resolveAsset('/characters/tactical_peanut_portrait.png'), description: 'Sniper Rifle — Headshot Bonus' },
  { id: 'sponge',    name: 'Fat Guy',           url: resolveAsset('/characters/sponge_peanut_portrait.png'),   description: 'Spatula Slash — Sponge Absorb' },
  { id: 'cowboy',    name: 'Cowboy',            url: resolveAsset('/characters/cowboy_peanut_portrait.png'),    description: 'The Revolver — Quick Draw' },
  { id: 'killa',     name: 'Duck',             url: resolveAsset('/characters/killa_peanut_portrait.png'),     description: 'RPK Machine Gun — Suppression Fire' },
  { id: 'wizard',    name: 'Robot Girl',            url: resolveAsset('/characters/wizard_peanut_portrait.png'),    description: 'Fireball Wand — Arcane Surge' },
  { id: 'viking',    name: 'Viking',            url: resolveAsset('/characters/viking_peanut_portrait.png'),    description: 'Viking Axe — Berserker Rage' },
  { id: 'mr_silly',  name: 'Gen-Z Coolkid',         url: resolveAsset('/characters/mr_silly_peanut_portrait.png'),  description: 'Rage Juice — Silly Juice' },
  { id: 'lebron',    name: 'Smokeshow',  url: resolveAsset('/characters/lebron_peanut_portrait.png'),    description: 'Tactical Basketball — King\'s Court' },
  { id: 'chad',      name: 'Buff Guy',              url: resolveAsset('/characters/chad_peanut_portrait.png'),      description: 'The Chad Aura — Golden Aura' },
  { id: 'pirate',    name: 'Pirate Peanut',            url: resolveAsset('/characters/pirate_peanut_portrait.png'),    description: 'The Blunderbuss — Scavenger' },
  { id: 'greg',        name: 'Greg',        url: resolveAsset('/characters/greg_peanut_portrait.png'),        description: 'Karen Drones — Micromanager' },
  { id: 'suit_guy',    name: 'Suit Guy',    url: resolveAsset('/characters/suit_guy_peanut_portrait.png'),    description: 'The Ex Girlfriend — Hostile Takeover' },
  { id: 'cheerleader', name: 'Cheerleader', url: resolveAsset('/characters/cheerleader_peanut_portrait.png'), description: 'Squirter — Spirit Fingers' },
  { id: 'bigfoot',     name: 'Bigfoot',     url: resolveAsset('/characters/bigfoot_peanut_portrait.png'),     description: 'Banana Bombs — Cryptid Chaos' },
];

export interface CharacterDef {
  id: string;
  name: string;
  url: string;
  description: string;
}

export interface RunStats {
  kills: number;
  floor: number;
  timeSurvived: number;
  characterId: string;
  level: number;
  chestsOpened: number;
  damageDealt: number;
}

export default function Home() {
  // BETA 16.83 — Per Dylan: "the sign in is gon on the burnt peanut
  // simulator. people can just play the game.... why?" Root cause:
  // BETA 13.61 (April 29) intentionally changed the initial screen
  // from 'auth' → 'menu' so the autonomous bot-training overnight
  // loop could programmatically start games via window.__startGame
  // without going through the auth screen. That bot-only convenience
  // shipped to all real users and silently broke auth gating for
  // 9 days. BETA 16.74's iPhone bounce-back comment ("starts at menu
  // anyway") didn't catch it.
  // Fix: default to 'auth'. Bot-farm mode (?botfarm=true) explicitly
  // skips to 'menu' since the bot iframe orchestrator calls
  // __startGame directly. Real users see the sign-in screen first
  // and can either log in or click "play as guest (no leaderboard)".
  // BETA 22.52 — NO auth wall at boot (Dylan: "we need to make something
  // seamless so players are not hit with something that kills the vibe").
  // Everyone lands in the menu; sign-in is an overlay that appears only when
  // it's needed — pressing START RUN while signed out (see showAuthOverlay).
  const [screen, setScreen] = useState<Screen>('menu');
  // Sign-in overlay + the run that triggered it (launched right after auth).
  const [showAuthOverlay, setShowAuthOverlay] = useState(false);
  const pendingStartRef = useRef<number | null>(null);
  const [selectedChar, setSelectedChar] = useState<CharacterDef | null>(null);
  const [prevScreen, setPrevScreen] = useState<Screen>('menu');
  const [showSettingsOverlay, setShowSettingsOverlay] = useState(false);
  // BETA — Dylan: "one Dev button on the main screen ... everything else should
  // live inside the dev button, its getting messy." Collapses all the bottom-left
  // dev tool buttons (+ the Replays panel) under a single toggle.
  const [devMenuOpen, setDevMenuOpen] = useState(false);

  // BETA 22.49 — Sorry Boss boot intro (BLAME CULTURE studio card → title video →
  // freeze + embers → menu). Shows once per browser session; force it for preview
  // with ?intro=1. Its own click-to-start also unlocks browser audio, so while it's
  // up we suppress the old LET'S RIDE overlay (they'd be two redundant click-gates).
  const _introForced = (() => { try { return new URLSearchParams(window.location.search).has('intro'); } catch { return false; } })();
  const [showIntro, setShowIntro] = useState<boolean>(() => {
    if (_introForced) return true;
    try { return sessionStorage.getItem('sb_intro_seen') !== '1'; } catch { return true; }
  });
  const dismissIntro = () => {
    setShowIntro(false);
    try {
      sessionStorage.setItem('sb_intro_seen', '1');
      // BETA 22.52 — if the tear revealed the AUTH screen (fresh app boot, no
      // session), the StartMenu isn't mounted yet, so its inputLocked-based
      // entrance can never fire. Leave a one-shot flag: the menu consumes it
      // on its first mount (post-sign-in) and plays the full entrance then.
      sessionStorage.setItem('sb_entrance_pending', '1');
    } catch { /* */ }
  };
  // BETA 16.56 — unified UserSettings via userSettings.ts. Loads anonymous
  // settings on first mount; reloaded keyed by username when login lands.
  const [userSettings, setUserSettings] = useState<UserSettings>(() => loadUserSettings(null));
  const audioSettings: AudioSettings = {
    masterVolume: userSettings.masterVolume,
    musicVolume: userSettings.musicVolume,
    sfxVolume: userSettings.sfxVolume,
  };
  const setAudioSettings = (next: AudioSettings) => {
    setUserSettings(prev => ({ ...prev, ...next }));
  };
  const hudScale = userSettings.hudScale;
  const setHudScale = (next: number) => {
    setUserSettings(prev => ({ ...prev, hudScale: next }));
  };
  const [postRunUnlocks, setPostRunUnlocks] = useState<string[]>([]);
  const [botMode, setBotMode] = useState(false);
  const [savedBotRuns, setSavedBotRuns] = useState(() => getSavedRunCount());
  // Meta-progression: lifetime stats, quest unlocks, goop currency
  const [goopState, setGoopState] = useState<GoopSystemState>(() => loadGoopState() ?? createGoopSystem());
  const [loggedIn, setLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  // BETA 16.02 — Per Dylan: "have a button that pops up when you first
  // launch or refresh the game. not when you go back to home scrren.
  // that is in the middle of the screen. blurr baclground. button in
  // focus. says LET'S RIDE." Acts as the user-activation gesture that
  // browsers require to unsuspend AudioContext. Dismissed on click.
  // BETA 16.74 — Per Dylan (iPhone): "puts be back in the lets ride
  // button." Persist the dismiss to sessionStorage so even if iOS
  // Safari force-reloads the PWA mid-session (memory pressure /
  // back-forward / etc) the user doesn\'t see it twice. sessionStorage
  // resets when the session ends (tab/browser closed) so a fresh
  // launch the next day still gets the gesture-unlock click. Use a
  // try/catch since some iOS modes block storage.
  // BETA 22.50 — LET'S RIDE overlay removed. The Sorry Boss boot intro's first
  // click now handles the browser audio-unlock gesture (IntroSequence.start →
  // resumeAudio + startBgm), so the old yellow "LET'S RIDE" gate over the
  // classroom is redundant. One fewer click-wall before the front-facing menu.

  // All character IDs in the game
  const ALL_CHARACTER_IDS = new Set(['tactical','sponge','cowboy','killa','wizard','viking','mr_silly','lebron','chad','pirate','greg','suit_guy','cheerleader','bigfoot']);

  // BETA 15.82 — Eager BGM preload + start on first user interaction.
  // Per Dylan: "audio took a few seconds to start when i got on the site."
  // Root cause: 32 MB BGM file was fetched + decoded ON the first user
  // click, adding 1-3s of waiting before music played.
  // Fix: split it into two phases.
  //   1. ON MOUNT (before any click): create AudioContext (starts suspended,
  //      no sound yet — that's fine), kick off fetch + decode in the
  //      background. By the time the user looks at the page and clicks
  //      anywhere, the buffer is decoded and ready.
  //   2. ON FIRST INTERACTION (any pointerdown / keydown): resume the
  //      suspended AudioContext + start BGM playback. With the buffer
  //      already decoded, playback is instant.
  // Both phases live in the same useEffect so cleanup is unified.
  useEffect(() => {
    // Phase 1: eager preload. initAudio() is idempotent — safe even though
    // GameCanvas calls it again later. AudioContext creation is allowed
    // before user gesture in modern browsers; it just stays suspended
    // until resume() is called inside a gesture.
    initAudio();
    // BETA 15.83 — Apply saved volume settings immediately so they take
    // effect from the lobby (was previously only applied on game start
    // via GameCanvas mount, leaving home-screen audio at defaults).
    setMasterVolume(audioSettings.masterVolume);
    setMusicVolume(audioSettings.musicVolume);
    setBgmVolume(audioSettings.musicVolume);  // BGM tracks the music slider
    setSFXVolume(audioSettings.sfxVolume);
    // BETA 21.08 — SLOW-CONNECTION GUARD. At menu open we used to eagerly fetch
    // ~57 MB of UNCOMPRESSED WAV in the background (BGM ~32 MB + death eulogy
    // ~25 MB). On a slow / data-saver link that starves the visible art — it's
    // exactly why a slow-net tester saw black character-portrait tiles. On slow
    // networks we now SKIP the eager audio preload; the tracks still load lazily
    // on first play. Fast networks (4g/unknown) keep the instant-start preload.
    const _net = getNetworkClass(); // '4g' | '3g' | '2g' | 'slow-2g' | '...+saveData' | 'unknown'
    const _slowNet = _net.includes('2g') || _net.includes('3g') || _net.includes('saveData');
    if (!_slowNet) {
      void preloadBgm();         // defaults to /sounds/bgm_bungulators_mute_mom.mp3
      void preloadDeathEulogy(); // 25 MB eulogy ready before the death screen
    }

    // Phase 2: start playback on first interaction.
    // BETA 15.98 — Per Dylan: "home screen song does not start until i hit
    // start in home screen when i refresh please fix. i should not have to
    // click start or anything for music to start in home screen.
    // automatically start imediatly once i am in game."
    //
    // The honest answer is browser autoplay policy: Chrome / Safari /
    // Firefox ALL block AudioContext.resume() until the user has performed
    // a real "user activation" gesture (click / pointerdown / keydown /
    // touchstart). mousemove + scroll do NOT count. There is no way to
    // start audio with sound on a fresh page load — this is a deliberate
    // browser security/UX policy. We can only minimize the gap between
    // user-arrives and music-starts.
    //
    // What we do here: listen for every plausible activation event with
    // CAPTURE phase (so we fire before any UI handler can stopPropagation)
    // on BOTH document AND window. Includes mobile/touch + accessibility
    // (keydown). First firing → resume audio + start BGM → remove all
    // listeners. So as soon as user moves their mouse to ANY click target
    // and clicks (start button, settings icon, anywhere on the page), the
    // music starts in the same frame.
    let started = false;
    const onFirstInteraction = () => {
      if (started) return;
      started = true;
      resumeAudio();
      void startBgm();
      teardown();
    };
    const events = ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'click'];
    const teardown = () => {
      for (const evt of events) {
        document.removeEventListener(evt, onFirstInteraction, true);
        window.removeEventListener(evt, onFirstInteraction, true);
      }
    };
    for (const evt of events) {
      document.addEventListener(evt, onFirstInteraction, true);
      window.addEventListener(evt, onFirstInteraction, true);
    }
    return teardown;
  }, []);

  // BETA 15.56 — Auto-unlock-all for dev users (DevPeanut, KaySeaBass, etc.)
  // Per Dylan: "can you unlock every item for me. that is my username
  // [DevPeanut]." Triggers on username change. Mutates goopState in place
  // and saves so the unlocks persist across sessions. Safe to re-run.
  // BETA 17.12 — PSBOT cycle-30ae: Dylan reported items still missing for
  // DevPeanut. Fix at applyDevUserUnlocks side (now also bumps lifetimeStats
  // + completedQuests so quest-gated unlocks fire too) PLUS explicit weapon
  // unlock via setUnlockedCharacterIds with ALL chars. Belt-and-suspenders
  // — three different unlock paths all guaranteed to run for dev users.
  useEffect(() => {
    if (!isDevUser(username)) return;
    setGoopState(prev => {
      const next = { ...prev };
      next.unlockedPercIds = new Set(prev.unlockedPercIds);
      next.unlockedItemIds = new Set(prev.unlockedItemIds);
      next.unlockedCharacterIds = new Set(prev.unlockedCharacterIds ?? []);
      next.completedQuests = new Set(prev.completedQuests ?? []);
      next.lifetimeStats = { ...prev.lifetimeStats };
      applyDevUserUnlocks(next);
      saveGoopState(next);
      return next;
    });
    // BETA 17.12 — also force-activate every weapon for dev users so the
    // weapons grid shows green checkmarks immediately (matches bot mode's
    // "show everything" pattern but without the bot-mode side effects).
    // BETA 21.61 — dev variant RESPECTS the dev's saved checkmarks (so unchecking
    // sticks) instead of force-checking everything every load.
    activateAllForDevMode();
  }, [username]);

  // Sync character unlocks + menu live data whenever goopState or botMode changes
  // BETA 17.12 — PSBOT cycle-30ae: dev users get the same all-unlocked
  // treatment as bot mode (without bot AI). Per Dylan: "everyone who has a
  // username and password that are devs should all have the same unlocks
  // everything." Previously dev users went through the "restore from
  // localStorage" path which lost weapons if their saved set was stale.
  // BETA 17.12 (revised post-cycle-30ae screenshot): also pass dev as bot-mode
  // to MenuRenderer so its INTERNAL checkItemUnlock / isPercUnlocked
  // functions return true. Otherwise items with unlock types MenuRenderer
  // doesn't recognize (only handles total_kills/wave/deaths/level/chests/dmg-taken
  // for items, plus survive_minutes/boss_killed/total_gold for percs) stay
  // locked visually even though they're "unlocked" in goopState.
  useEffect(() => {
    const isDev = isDevUser(username);
    setMenuBotMode(botMode || isDev); // dev users see all-unlocked in grid display
    if (botMode || isDev) {
      // Bot mode OR dev: unlock every character, perc, item, and weapon.
      // ALL_CHARACTER_IDS includes every char which signals to
      // setUnlockedCharacterIds to force-unlock shared weapons too.
      setUnlockedCharacterIds(ALL_CHARACTER_IDS, goopState.lifetimeStats);
      // BETA 21.61 — true bot/lab → full pool (no persistence). Dev human → respect
      // saved checkmarks so unchecking sticks.
      if (botMode) activateAllForBotMode();
      else activateAllForDevMode();
    } else {
      // Normal mode: restore whatever the player had saved.
      // BETA 19.35 — pass lifetimeStats so shared weapons unlock when their
      // quest condition is met (was unconditionally unlocked before).
      const charIds = goopState.unlockedCharacterIds ?? new Set<string>();
      setUnlockedCharacterIds(charIds, goopState.lifetimeStats);
      restoreLoadoutFromStorage();
    }
    setMenuLiveData(goopState.lifetimeStats, goopState.completedQuests);
  }, [goopState, botMode, username]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check existing session on mount.
  // BETA 16.74 — Per Dylan (iPhone): "when i click lets ride and chose
  // character and try to get in game it puts be back in the lets ride
  // button." Root cause: this Supabase auth-restore promise resolved
  // 1-3 seconds AFTER mount on slow connections. If the user navigated
  // forward before resolution, a late setScreen('menu') yanked them
  // back. Fix at the time was to drop the setScreen call entirely.
  // BETA 16.83 — But that left no path from initial 'auth' → 'menu'
  // for users who have a saved session. Now: initial is 'auth' (so
  // non-logged-in users actually SEE the sign-in screen), and the
  // session-restore promise navigates to 'menu' ONLY IF the user is
  // still on 'auth'. Functional setScreen reads the live screen so
  // there's no stale-closure race. If the user already clicked "play
  // as guest" or navigated past auth, the late-resolved session won't
  // yank them back — preserving the iPhone fix.
  // BETA 16.88 — Per Dylan: "when i try and play a game now it takes
  // a long time to load in." Kick off a background preload of all
  // static game assets (floor tiles, border walls, shrines, item icons,
  // perc icons) the moment Home mounts. By the time the player
  // navigates auth → menu → character select → loadout → DEPLOY →
  // 3-sec countdown → initGame, ~60+ images are already in imageCache
  // and initGame's Promise.allSettled resolves instantly. No UI
  // blocking, no awaiting on this — fire and forget.
  useEffect(() => {
    preloadStaticGameAssets().catch(() => { /* never crash on preload */ });
  }, []);

  // BETA 21.06 — restore a saved tester-code session BEFORE the Supabase check
  // so code-testers land in the menu instantly (no email/account round-trip).
  useEffect(() => {
    try {
      const devName = localStorage.getItem('goopzone_dev_session');
      if (devName && isDevUser(devName)) {
        setUsername(devName);
        setLoggedIn(true);
        setScreen(prev => prev === 'auth' ? 'menu' : prev);
      }
    } catch { /* private mode */ }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        supabase.from('profiles').select('username').eq('id', session.user.id).single()
          .then(({ data }) => {
            setUsername(data?.username ?? session.user.email?.split('@')[0] ?? 'Player');
            setLoggedIn(true);
            // Only auto-advance from 'auth' → 'menu'. If user is already
            // past auth (skipped or otherwise), don't yank them back.
            setScreen(prev => prev === 'auth' ? 'menu' : prev);
          });
      }
    });
  }, []);

  // BETA 22.52 — after ANY successful sign-in, close the overlay and — if a
  // START RUN press is what summoned it — launch that run immediately, so
  // auth feels like a speed bump on the way in, not a wall.
  const _finishAuth = useCallback((uname: string) => {
    setUsername(uname);
    setLoggedIn(true);
    setShowAuthOverlay(false);
    const idx = pendingStartRef.current;
    pendingStartRef.current = null;
    if (idx != null && idx >= 0 && idx < CHARACTERS.length) {
      initAudio();
      setSelectedChar(CHARACTERS[idx]);
      setScreen('game');
    }
  }, []);

  const handleAuthSuccess = useCallback((uname: string) => { _finishAuth(uname); }, [_finishAuth]);

  // BETA 21.06 — tester-code login: a valid code resolved to a dev username in
  // AuthScreen. Remember it on this device ('locked in') so the tester only
  // enters the code once. No Supabase involved.
  const handleDevCode = useCallback((uname: string) => {
    try { localStorage.setItem('goopzone_dev_session', uname); } catch { /* private mode */ }
    _finishAuth(uname);
  }, [_finishAuth]);

  // START RUN pressed while signed out → remember the run, raise the sign-in.
  const handleRequestSignIn = useCallback((charIdx?: number) => {
    pendingStartRef.current = charIdx ?? null;
    setShowAuthOverlay(true);
  }, []);

  const handleSkipAuth = useCallback(() => {
    setUsername('Guest');
    setLoggedIn(false);
    setScreen('menu');
  }, []);

  const handleSignOut = useCallback(async () => {
    try { localStorage.removeItem('goopzone_dev_session'); } catch { /* private mode */ } // BETA 21.06 — clear tester-code session
    await signOut();
    setLoggedIn(false);
    setUsername('');
    // BETA 22.52 — no auth wall anymore: signing out just leaves you browsing
    // the menu as a guest; sign-in comes back when you press START RUN.
  }, []);

  // Called when PLAY is clicked — must call initAudio() here (direct user gesture)
  const handleStartGame = useCallback((charIdx: number) => {
    if (charIdx >= 0 && charIdx < CHARACTERS.length) {
      initAudio();
      setSelectedChar(CHARACTERS[charIdx]);
      setScreen('game');
    }
  }, []);

  const handleBackToMenu = useCallback(() => {
    setShowSettingsOverlay(false);
    setScreen('menu');
    // Refresh the saved run count so the CSV button updates
    setSavedBotRuns(getSavedRunCount());
  }, []);

  // BETA 13.84 / 13.85 — BOT FARM AUTO-MODE. When the page loads with
  // `?botfarm=true&character=X&build=Y&runs=N&tile=T` this iframe is being
  // driven by the /bot-farm dashboard. We:
  //   (1) BETA 13.85: set `botMode` state to TRUE BEFORE handleStartGame so
  //       GameCanvas mounts with `botModeRef.current=true` and auto-toggles
  //       bot on at init (no race; no manual click needed).
  //   (2) Force 1× speed (memory rule).
  //   (3) Start the requested character.
  //   (4) Poll bot.allRuns (in-memory, NOT localStorage — see autoPlaytester
  //       BETA 13.85 botfarm-isolation skip on saveRunsToStorage) for run
  //       count progress and postMessage `farm-progress` to window.parent.
  //   (5) When run count hits target, postMessage `farm-complete` with the
  //       per-iframe CSV string. Each iframe's CSV is independent — the
  //       dashboard concats them into a master.
  //
  // BETA 13.85: REMOVED the `localStorage.removeItem(...)` calls. They were
  // racing across all 10 iframes (same origin = shared localStorage), causing
  // tiles to wipe each other's results. Per-iframe state lives only in the
  // module-scoped `bot.allRuns` array now (see autoPlaytester botfarm flag).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('botfarm') !== 'true') return;
    // BETA 17.41 — click-sweep harness needs to test the MAIN MENU without
    // the botfarm useEffect auto-starting a chad run. New `nostart=true`
    // flag skips this auto-start path but keeps the auth bypass. Used
    // by scripts/click-sweep.mjs to sweep menu buttons; programmatic
    // __startGame() calls still work for the smoke harness.
    if (params.get('nostart') === 'true') return;
    const charId = params.get('character') ?? 'chad';
    const build = (params.get('build') ?? 'adaptive') as Parameters<typeof import('../game/autoPlaytester')['setBotBuild']>[0];
    const targetRuns = parseInt(params.get('runs') ?? '5', 10);
    const tileId = params.get('tile') ?? charId;

    const run = async () => {
      const apMod = await import('../game/autoPlaytester');
      apMod.setBotBuild(build);

      // Tell parent we're starting
      window.parent?.postMessage({ type: 'farm-status', tile: tileId, status: 'starting', target: targetRuns }, '*');

      // Force 1× per memory rule
      const dbg = await import('../game/debugState');
      dbg.setDebugTimeScale(1);

      const idx = CHARACTERS.findIndex(c => c.id === charId);
      if (idx < 0) {
        window.parent?.postMessage({ type: 'farm-error', tile: tileId, error: `unknown character ${charId}` }, '*');
        return;
      }
      // BETA 13.85 fix: enable bot mode BEFORE starting game so GameCanvas
      // mounts with botModeRef.current=true and auto-toggles bot on at init.
      // Previously this useEffect did handleStartGame() then toggled bot 1.2s
      // later, which had a race with GameCanvas's own effects and often left
      // the bot disabled requiring a manual click.
      setBotMode(true);
      handleStartGame(idx);

      // Belt-and-suspenders: explicit toggle after engine warmup, in case
      // setBotMode raced with the React render. isBotEnabled() short-circuits
      // if bot is already on (no double-toggle).
      await new Promise(r => setTimeout(r, 1500));
      if (!apMod.isBotEnabled()) apMod.toggleBot();

      // BETA 13.100c — partial-save on STOP. Listen for `farm-stop-request`
      // from the dashboard. When received, snapshot whatever's in bot.allRuns
      // (could be 0, 2, partway through 5, etc.), disable bot, post one
      // farm-complete with the partial CSV, then no-op the rest of the run
      // loop. Without this, stopping the farm early throws all the partial
      // run data away.
      let _stoppedEarly = false;
      const onStopReq = (ev: MessageEvent) => {
        const d = ev?.data;
        if (!d || d.type !== 'farm-stop-request' || _stoppedEarly) return;
        _stoppedEarly = true;
        try {
          if (apMod.isBotEnabled()) apMod.toggleBot();
        } catch { /* noop */ }
        const runs = apMod.getBotRunLogs();
        const csv = apMod.exportCSVAsString(runs);
        window.parent?.postMessage({ type: 'farm-complete', tile: tileId, csv, runCount: runs.length }, '*');
      };
      window.addEventListener('message', onStopReq);

      // BETA 13.85 — FPS measurement loop. We track rAF timestamps over a rolling
      // 1-second window so the dashboard's perf panel can show per-tile fps and
      // catch browser throttling in real time. Critical signal for the user to
      // know if their hardware is actually pushing the game vs being bottlenecked.
      let _fpsFrames = 0;
      let _fpsLastReset = performance.now();
      let _fps = 0;
      const fpsLoop = () => {
        _fpsFrames++;
        const now = performance.now();
        if (now - _fpsLastReset >= 1000) {
          _fps = _fpsFrames;
          _fpsFrames = 0;
          _fpsLastReset = now;
        }
        requestAnimationFrame(fpsLoop);
      };
      requestAnimationFrame(fpsLoop);

      // Poll for run completion. Each finalized run lives in bot.allRuns (in-memory
      // only — see autoPlaytester BETA 13.85 botfarm-mode skip on saveRunsToStorage).
      const startTime = Date.now();
      const poll = setInterval(() => {
        const runs = apMod.getBotRunLogs();
        const count = runs.length;
        const cur = apMod.getCurrentRunLog();
        // Memory usage (Chrome-only API, undefined in Safari/Firefox)
        const perf = (performance as Performance & { memory?: { usedJSHeapSize: number } });
        const memMB = perf.memory ? Math.round(perf.memory.usedJSHeapSize / 1024 / 1024) : 0;
        window.parent?.postMessage({
          type: 'farm-progress',
          tile: tileId,
          completed: count,
          target: targetRuns,
          inFlightSurv: cur ? Math.round(cur.survivalTimeSec ?? 0) : 0,
          elapsedSec: Math.round((Date.now() - startTime) / 1000),
          fps: _fps,
          memMB,
        }, '*');
        if (count >= targetRuns) {
          clearInterval(poll);
          // Disable bot so the engine stops looping, then post final CSV.
          if (apMod.isBotEnabled()) apMod.toggleBot();
          const csv = apMod.exportCSVAsString(runs);
          window.parent?.postMessage({ type: 'farm-complete', tile: tileId, csv, runCount: count }, '*');
        }
      }, 1000); // BETA 13.85: tightened from 2000ms → 1000ms so the perf panel updates 1Hz
    };

    void run();
  }, [handleStartGame]);

  // BETA 13.61: programmatic game control for autonomous bot training loops.
  // Lets external orchestration (Claude session via preview_eval) start a game
  // with a specific character without canvas-clicking, switch characters between
  // runs, and query game state. Used by the overnight 30-runs-per-character cycle.
  useEffect(() => {
    type StartBuildArgs = {
      buildName?: string;
      weapons: string[];
      /** BETA 19.87 — Per-weapon target level. Replica needs to call
       *  levelUpWeapon N times to reach the original run's weapon level
       *  (basketball:9, squirter:9, etc.). Without this, all weapons spawn
       *  at L1 and the replica is dramatically under-powered. */
      weaponLevels?: Record<string, number>;
      items: string[];
      /** BETA 19.90 — Per-item stack override. applyBuild defaults to
       *  `maxStacks` (99 for "free stack" stat items), but the player only
       *  picked C05 ONCE in the real run. Replay stacking it 99 times made
       *  xpMultiplier 24.96 vs actual 1.44 (17× wrong) and similar compound
       *  errors on damageBonus + attackSpeed. This map carries the exact
       *  pick count per item from the run's itemsCollected so the replica
       *  matches reality. */
      itemStacks?: Record<string, number>;
      /** Shrine IDs — DUPLICATES STACK. Pass the raw pick list (e.g.
       *  ['projcnt', 'projcnt', 'projcnt', 'projcnt'] for 4 projectile-count
       *  shrines), NOT a deduped set. applyBuild calls applyShrineBuffById
       *  for each entry, and each call accumulates the buff. */
      shrines?: string[];
      percs?: string[];
      percLevel?: number;
      /** BETA 19.80 — Simulate run-state at N seconds in. Sets state.time
       *  + state.startTime + waveState.totalRunMinutes so spawn rate,
       *  difficulty curve, and per-tick wall-clock difficulty all reflect
       *  the late-game pressure the loadout was built for. Default 0
       *  (fresh run, original behavior). For replay: half the survival
       *  time or 360s, whichever is smaller. */
      startAtTimeSec?: number;
      /** BETA 19.80 — Optional player level override so enemy-alive cap
       *  (min(timeCap, 12 + level × 6)) lines up with where the loadout
       *  was earned. Default: leave at 1 (let XP from kills ramp). */
      playerLevel?: number;
      enableBot?: boolean;
    };
    type RunSummary = {
      id: string;
      createdAt: string;
      character: string;
      betaVersion: string;
      survivalSec: number;
      kills: number;
      level: number;
      damage: number;
      weapons: string[];
      itemCount: number;
      percCount: number;
      shrineCount: number;
    };
    type W = typeof window & {
      __startGame?: (charId: string | number) => boolean;
      __startWithBuild?: (charId: string | number, build: StartBuildArgs) => Promise<boolean>;
      __replayLastRun?: (opts?: { startAtTimeSec?: number; playerLevel?: number }) => Promise<boolean>;
      __listRecentRuns?: (opts?: { limit?: number; character?: string; user?: string }) => Promise<RunSummary[]>;
      __replayRun?: (id: string, opts?: { startAtTimeSec?: number; playerLevel?: number }) => Promise<boolean>;
      /** BETA 19.92 — Compare replica end-state vs original run finalStats.
       *  Called AFTER a replay has been running long enough to stabilize
       *  (~5-10 sec post-spawn). Outputs a diff table to console.table.
       *  Powers Dylan's "find why my run broke" workflow — when the replay
       *  numbers don't match reality, the diff highlights the divergence. */
      __replayDiff?: (originalRunId?: string) => Promise<Record<string, { real: number; replica: number; deltaPct: number }> | null>;
      __returnToMenu?: () => void;
      __getGamePhase?: () => string;
      __getGameScreen?: () => string;
      __setTimeScale?: (v: number) => void;
      __getTimeScale?: () => number;
    };
    const w = window as W;
    w.__startGame = (charId: string | number): boolean => {
      const idx = typeof charId === 'number'
        ? charId
        : CHARACTERS.findIndex(c => c.id === charId);
      if (idx < 0 || idx >= CHARACTERS.length) {
        console.warn(`[__startGame] unknown character "${charId}" — valid:`, CHARACTERS.map(c => c.id));
        return false;
      }
      handleStartGame(idx);
      return true;
    };

    // BETA 19.77 — Dylan: "WHY is the bot not asrating with my exact build for
    // the first second... cant you build it so that when you start a bot run
    // game it is the exact build from the jump". Start a game AND pre-load
    // a full WeaponLab build (weapons + items + shrines + percs at any level)
    // before the bot's first tick, so testing replays your exact loadout
    // from frame 1 instead of watching a 7-min ramp-up.
    w.__startWithBuild = async (charId, build) => {
      const idx = typeof charId === 'number'
        ? charId
        : CHARACTERS.findIndex(c => c.id === charId);
      if (idx < 0 || idx >= CHARACTERS.length) {
        console.warn(`[__startWithBuild] unknown character "${charId}"`);
        return false;
      }
      handleStartGame(idx);
      // BETA 19.84 — Wait for engine to STABLY reach the playing phase before
      // applying. Previously polled for __getPlayer (truthy as soon as player
      // exists), but initGame keeps running post-init steps (starter weapon
      // equip, state reset) AFTER player is constructed. Race: applyBuild
      // overwritten by later init steps → weapons + level + invinc all reset.
      //
      // Fix: poll for engine phase === 'playing' (only set after FULL init
      // completes), then wait an additional 400ms for any trailing async
      // setup (sprite preload, character-passive init) to finish.
      const labMod = await import('@/game/weaponLab');
      const apMod  = await import('@/game/autoPlaytester');
      const w2 = window as Window & { __getEnginePhase?: () => string | null };
      const startedAt = performance.now();
      while (performance.now() - startedAt < 5000) {
        await new Promise(r => setTimeout(r, 80));
        if (w2.__getEnginePhase?.() === 'playing') break;
      }
      // Extra settle: 400ms after phase=='playing' so trailing init steps land
      // before applyBuild touches state. Without this, the starter-weapon
      // equip from initGame races with applyBuild's setWeaponEquipped calls
      // and the wrong weapon slot ends up final.
      await new Promise(r => setTimeout(r, 400));
      try {
        labMod.applyBuild({
          buildName: build.buildName ?? 'start-with-build',
          buildSource: 'preset',
          weapons: build.weapons,
          items: build.items,
          shrines: build.shrines ?? [],
          percs: build.percs ?? [],
          percLevel: build.percLevel ?? 6,
        });
      } catch (e) {
        console.warn('[__startWithBuild] applyBuild threw', e);
        return false;
      }

      // BETA 19.90 — Per-item stack count from actual run. applyBuild
      // defaults each item to maxStacks (often 99 for "free stack" stat
      // items). The real run only picked C05 ONCE → 1 stack, not 99.
      // 99× +20% xpGain compounded to 24× xpMultiplier in the replica vs
      // 1.44× in the original. After applyBuild stacks at max, we override
      // each item to its actual pick count.
      if (build.itemStacks && Object.keys(build.itemStacks).length > 0) {
        try {
          for (const [itemId, stackCount] of Object.entries(build.itemStacks)) {
            labMod.setItemStacks(itemId, Math.max(1, stackCount));
          }
          console.log('[__startWithBuild] applied per-item stacks:', build.itemStacks);
        } catch (e) {
          console.warn('[__startWithBuild] setItemStacks threw', e);
        }
      }

      // BETA 19.87 — Level up weapons to match the original run. applyBuild
      // equips each weapon at L1; the player's actual run had them at L8/9.
      // Without this, replica DPS is dramatically lower than the run we're
      // trying to reproduce. Calls levelUpWeapon N times per weapon (each
      // call writes a card-rarity history entry + increments stats).
      if (build.weaponLevels && Object.keys(build.weaponLevels).length > 0) {
        try {
          const wpnMod = await import('@/game/weapons');
          // weaponLab re-exports the getter through getEquippedWeapons-style
          // accessors; for our needs we use a window-helper getter.
          type WInst = { definitionId: string; level: number };
          const inv = (window as Window & { __getInventorySnapshot?: () => { weapons?: WInst[] } | null }).__getInventorySnapshot?.();
          // The snapshot returns clones. Need the LIVE instances. Pull from
          // weaponLab (which uses the same module-instance the engine does).
          const labMod2 = await import('@/game/weaponLab');
          const allEq = labMod2.getEquippedWeaponIds?.() ?? [];
          // Iterate by id via the snapshot's list (level info comes from inv)
          // — but to mutate live we need the actual ws.weapons. Easiest: a
          // tiny new helper would be ideal but for now we route through the
          // window helper that GameCanvas exposes — __getPlayer returns live
          // state.player; we can fetch state via a similar pattern.
          const wsState = (window as Window & { __getWeaponSystemStateLive?: () => { weapons?: Array<{ definitionId: string; level: number }> } | null }).__getWeaponSystemStateLive?.();
          if (wsState?.weapons) {
            let totalLevelUps = 0;
            for (const wInst of wsState.weapons) {
              const targetLvl = build.weaponLevels[wInst.definitionId];
              if (!targetLvl || targetLvl <= wInst.level) continue;
              const stepsNeeded = targetLvl - wInst.level;
              for (let i = 0; i < stepsNeeded; i++) {
                const ok = wpnMod.levelUpWeapon(wInst as unknown as Parameters<typeof wpnMod.levelUpWeapon>[0], 'rare');
                if (!ok) break;
                totalLevelUps++;
              }
            }
            console.log('[__startWithBuild] leveled up weapons:', totalLevelUps, 'total bumps, final levels:', wsState.weapons.map(w => `${w.definitionId}:L${w.level}`).join(', '));
          } else {
            console.warn('[__startWithBuild] no live weapon state for level-up, inv=', inv?.weapons?.length, 'allEq=', allEq.length);
          }
        } catch (e) {
          console.warn('[__startWithBuild] weapon level-up threw', e);
        }
      }

      // BETA 19.83 — Replica spawn-safety. With startAtTimeSec=727s (Dylan's
      // LeBron death point), the player materialized into 219 enemies and
      // got insta-killed before the bot's first tick. Replicas are useless
      // if they end before frame 5. Grant 15s invincibility + clear enemies
      // within 700wu so the bot can actually engage the loadout. Fires on
      // any start-with-build call (time-warped OR not), since even a fresh
      // L1 start with this loadout would be unfair if enemies spawned on top.
      try {
        const p = (window as Window & { __getPlayer?: () => { invincibleTimer?: number; hp?: number; maxHp?: number; x?: number; y?: number } }).__getPlayer?.();
        if (p) {
          p.invincibleTimer = Math.max(p.invincibleTimer ?? 0, 15);
          p.hp = p.maxHp ?? p.hp ?? 100;
          // Clear enemies within 700wu so the bot gets breathing room.
          const enemiesMod = await import('@/game/enemyAdapter');
          const wsAny = enemiesMod.getEnemySystemState?.() as { enemies?: Array<{ alive?: boolean; x?: number; y?: number }> } | null | undefined;
          if (wsAny?.enemies && p.x != null && p.y != null) {
            const px = p.x, py = p.y;
            let cleared = 0;
            for (const e of wsAny.enemies) {
              if (!e.alive) continue;
              const dx = (e.x ?? 0) - px, dy = (e.y ?? 0) - py;
              if (dx * dx + dy * dy < 700 * 700) { e.alive = false; cleared++; }
            }
            console.log('[__startWithBuild] spawn-safety: 15s invinc + cleared', cleared, 'nearby enemies');
          }
        }
      } catch (e) {
        console.warn('[__startWithBuild] spawn-safety threw', e);
      }
      // BETA 19.80 — Time-warp the simulation forward. applyBuild ALREADY
      // stacked all shrines + leveled percs + max-stacked items, so the
      // PLAYER POWER reflects the loadout. Now we also need the WORLD STATE
      // (spawn rate, difficulty multiplier, wave system, alive cap) to
      // reflect the moment in the run when that loadout was earned. Setting
      // state.time + state.startTime + waveState.totalRunMinutes does this.
      if ((build.startAtTimeSec ?? 0) > 0 || (build.playerLevel ?? 0) > 1) {
        try {
          // Reach into the live engine state via the same module reference
          // GameCanvas uses (engine.ts owns initGame + the active state ref).
          const eng = await import('@/game/engine') as unknown as { GAME_VERSION: string } & Record<string, unknown>;
          void eng; // engine doesn't expose a state-getter — we mutate via the GameCanvas-owned state
          // Easiest path: use the window helpers — __getPlayer returns the live state.player by ref.
          const p = (window as Window & { __getPlayer?: () => { level?: number; xp?: number; xpToNext?: number } }).__getPlayer?.();
          if (p && (build.playerLevel ?? 0) > 1) {
            p.level = build.playerLevel ?? 1;
            // Reset XP bar to start of that level so next kill = lvl+1 progression
            p.xp = 0;
            // xpToNext scales with level — use a rough formula matching engine
            // (engine handles its own xpToNext on level-up, but seeding helps).
            p.xpToNext = Math.round(100 * Math.pow(1.15, (p.level ?? 1) - 1));
          }
          // For state.time / startTime, expose via a new tiny helper
          const tw = window as Window & {
            __setRunTime?: (sec: number) => void;
          };
          if (tw.__setRunTime && (build.startAtTimeSec ?? 0) > 0) {
            tw.__setRunTime(build.startAtTimeSec ?? 0);
          }
        } catch (e) {
          console.warn('[__startWithBuild] time-warp threw', e);
        }
      }

      if (build.enableBot !== false && !apMod.isBotEnabled()) apMod.toggleBot();
      console.log('[__startWithBuild] applied:', build.buildName, build);
      return true;
    };

    // BETA 19.77 — Auto-fetch the latest Supabase run for this user + convert
    // it into a start-with-build call. Saves Dylan from having to construct
    // the loadout payload by hand. Picks the most recent live_play row for
    // username "DevPeanut" (or current username).
    // BETA 19.78 — Split into reusable pieces:
    //   _spawnFromRow(row)     — turns a Supabase row into __startWithBuild args
    //   __listRecentRuns(opts) — list summaries (id + key metadata) for picking
    //   __replayRun(id)        — replay a specific row by ID
    //   __replayLastRun()      — sugar for "most recent" (calls list + replay)
    const SUPABASE_URL = 'https://wslppieqghdyaodhbrbl.supabase.co';
    const SUPABASE_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || 'sb_publishable_7uHojLtW1sWE9risVX2CcA_Tmqk6sgY';
    // BETA 19.79 — Actual Supabase schema. Row columns are {id, reporter,
    // category, title, description, screen, created_at}. The reporter field
    // encodes user as "live-play:<username>" (or "bot:main"). The description
    // is a JSON STRING (not a JSONB column) holding the full run payload.
    // 19.77/19.78 assumed a richer schema (username/character columns,
    // run_data JSONB) that doesn't exist — both __replayLastRun and
    // __listRecentRuns 400'd. Fixed: filter by reporter, JSON.parse(desc).
    type SupaRow = {
      id?: string;
      reporter?: string;
      category?: string;
      title?: string;
      description?: string;
      created_at?: string;
    };
    type RunPayload = {
      characterId?: string;
      characterName?: string;
      gameVersion?: string;
      survivalTimeSec?: number;
      killCount?: number;
      // BETA 19.81 — actual field is `levelReached`, NOT `finalLevel`
      levelReached?: number;
      weaponsUsed?: string[];
      itemsCollected?: string[];
      percLevels?: string[];
      // BETA 19.87 — weaponTelemetry is an array of strings like
      // "tactical_basketball:lvl9,dmg=4867592,hit=4503/4949(91%),crit=583".
      // Parsing this lets the replay restore weapon LEVELS too (real run
      // had basketball L9 / squirter L9 / chad_aura L8 / viking_axe L8 —
      // replica had them all at L1, dramatically under-powered).
      weaponTelemetry?: string[];
      // BETA 19.81 — actual field is `pickEvents`, NOT `pickHistory`. This
      // was THE bug causing "0 shrines (RAW)" in the replica — wrong key
      // meant the shrine extraction loop found nothing. Live tracker
      // confirmed firing on all 4 sources (percs, weapons, shrines, chests)
      // — so the data was always there, just under the right name.
      pickEvents?: Array<{ source?: string; pickedId?: string; stat?: string; rarity?: string; delta?: number; t?: number }>;
    };

    const _parseRow = (r: SupaRow): RunPayload | null => {
      try { return JSON.parse(r.description ?? '{}') as RunPayload; }
      catch { return null; }
    };

    const _spawnFromRow = async (r: SupaRow, opts?: { startAtTimeSec?: number; playerLevel?: number }): Promise<boolean> => {
      const data = _parseRow(r);
      if (!data) { console.warn('[replay] failed to parse row', r.id); return false; }
      const weapons = (data.weaponsUsed ?? []).filter(Boolean);
      // BETA 19.87 — parse "tactical_basketball:lvl9,dmg=..." into
      // { tactical_basketball: 9, wpn_squirter: 9, chad_aura: 8, viking_axe: 8 }
      const weaponLevels: Record<string, number> = {};
      for (const entry of (data.weaponTelemetry ?? [])) {
        const [id, rest] = entry.split(':');
        if (!id || !rest) continue;
        const m = rest.match(/lvl(\d+)/);
        if (m) weaponLevels[id] = parseInt(m[1], 10);
      }
      // BETA 19.90 — Count item occurrences (each appearance = +1 stack).
      // Dylan picked C07 ×4, R02 ×3, C05 ×1 etc. Without per-item stack
      // counts, applyBuild defaulted to maxStacks=99 for each item,
      // causing the xpMult/atkSpeed/dmg compound bugs. itemStacks now
      // preserves the actual pick distribution from the run.
      const itemStacks: Record<string, number> = {};
      for (const id of (data.itemsCollected ?? []).filter(Boolean)) {
        itemStacks[id] = (itemStacks[id] ?? 0) + 1;
      }
      const items = Object.keys(itemStacks);
      // BETA 19.81 — Read the RIGHT field: live tracker writes `pickEvents`,
      // not `pickHistory` (the latter was my hallucinated field name).
      const events = data.pickEvents ?? [];
      const shrinesRaw = events
        .filter(p => p.source === 'shrine' && !!p.pickedId)
        .map(p => p.pickedId as string);
      const percs = (data.percLevels ?? []).map(s => s.split(':')[0]).filter(Boolean);
      const maxPercLevel = Math.max(...(data.percLevels ?? []).map(s => parseInt(s.split(':')[1] ?? '0', 10) || 0), 6);
      const charId = data.characterId || 'lebron';
      // BETA 19.82 — Dylan: "set it up to time of death to be the starting
      // time." Default time-warp is now the FULL survival time, so the replay
      // starts at the exact pressure (spawn rate, difficulty multiplier,
      // wave-system clock) the player was facing at the moment of death.
      // User can still override via opts.startAtTimeSec.
      const survival = data.survivalTimeSec ?? 0;
      const startAtTimeSec = opts?.startAtTimeSec ?? Math.round(survival);
      // Player level: use the actual `levelReached` from the run (was capped
      // at 30 in 19.80 for "alive-cap sanity" but Dylan wants real conditions).
      const playerLevel = opts?.playerLevel ?? Math.max(1, data.levelReached ?? Math.round(events.length / 2));
      console.log(`[replay] spawning ${charId} from row ${r.id?.slice(0, 8)} (${data.gameVersion}) — ${weapons.length} wpns, ${items.length} items, ${percs.length} percs L${maxPercLevel}, ${shrinesRaw.length} shrines (RAW), time-warp +${startAtTimeSec}s, player L${playerLevel}`);
      return await (w.__startWithBuild?.(charId, {
        buildName: `replay:${charId}@${data.gameVersion ?? '?'}:${r.id?.slice(0, 8)}`,
        weapons,
        weaponLevels,
        items,
        itemStacks,
        shrines: shrinesRaw,
        percs,
        percLevel: maxPercLevel,
        startAtTimeSec,
        playerLevel,
        enableBot: true,
      }) ?? false);
    };

    w.__listRecentRuns = async (opts) => {
      // Fetch more than asked so we can client-side filter by character
      // (character lives inside description JSON, so PostgREST can't filter).
      const wantLimit = Math.min(opts?.limit ?? 20, 50);
      const fetchLimit = opts?.character ? Math.min(wantLimit * 5, 100) : wantLimit;
      const targetUser = opts?.user || username || 'DevPeanut';
      const reporterFilter = `reporter=eq.${encodeURIComponent('live-play:' + targetUser)}`;
      try {
        const url = `${SUPABASE_URL}/rest/v1/bug_reports?select=id,reporter,category,title,description,created_at&order=created_at.desc&limit=${fetchLimit}&${reporterFilter}&category=eq.live_play&apikey=${SUPABASE_KEY}`;
        const res = await fetch(url);
        if (!res.ok) { console.warn('[__listRecentRuns]', res.status, await res.text()); return []; }
        const rows = (await res.json()) as SupaRow[];
        if (!Array.isArray(rows)) return [];
        const parsed = rows
          .map(r => ({ r, data: _parseRow(r) }))
          .filter(x => !!x.data) as Array<{ r: SupaRow; data: RunPayload }>;
        const filtered = opts?.character
          ? parsed.filter(x => x.data.characterId === opts.character)
          : parsed;
        const summaries: RunSummary[] = filtered.slice(0, wantLimit).map(({ r, data }) => {
          const events = data.pickEvents ?? [];
          const shrinesAll = events.filter(p => p.source === 'shrine' && !!p.pickedId);
          return {
            id: r.id ?? '',
            createdAt: r.created_at ?? '',
            character: data.characterId ?? '?',
            betaVersion: data.gameVersion ?? '?',
            survivalSec: data.survivalTimeSec ?? 0,
            kills: data.killCount ?? 0,
            level: data.levelReached ?? 0,
            damage: 0, // not consistently present at top-level of payload
            weapons: (data.weaponsUsed ?? []).filter(Boolean),
            itemCount: new Set((data.itemsCollected ?? []).filter(Boolean)).size,
            percCount: (data.percLevels ?? []).length,
            // shrineCount = TOTAL shrine picks (not unique), matches what
            // the replica will actually stack via the raw pickEvents list.
            shrineCount: shrinesAll.length,
          };
        });
        console.log(`[__listRecentRuns] ${summaries.length} runs for ${targetUser}` + (opts?.character ? ` (${opts.character})` : ''));
        console.table(summaries.map(s => ({
          id: s.id.slice(0, 8),
          char: s.character,
          beta: s.betaVersion,
          survSec: Math.round(s.survivalSec),
          lvl: s.level,
          kills: s.kills,
          when: s.createdAt.replace('T', ' ').slice(0, 19),
        })));
        return summaries;
      } catch (e) {
        console.warn('[__listRecentRuns] fetch threw', e);
        return [];
      }
    };

    w.__replayRun = async (id, opts) => {
      if (!id) { console.warn('[__replayRun] missing id'); return false; }
      try {
        const url = `${SUPABASE_URL}/rest/v1/bug_reports?select=*&id=eq.${encodeURIComponent(id)}&apikey=${SUPABASE_KEY}`;
        const res = await fetch(url);
        if (!res.ok) { console.warn('[__replayRun]', res.status, await res.text()); return false; }
        const rows = (await res.json()) as SupaRow[];
        if (!Array.isArray(rows) || rows.length === 0) {
          console.warn('[__replayRun] no row found for id', id);
          return false;
        }
        return await _spawnFromRow(rows[0], opts);
      } catch (e) {
        console.warn('[__replayRun] fetch threw', e);
        return false;
      }
    };

    // BETA 19.92 — Replay fidelity diff. Compares live replica stats vs the
    // original Supabase run's finalStats. Highlights divergences so Dylan
    // can see at a glance: "you said replica = 129% dmg, real = 79% dmg,
    // delta +63%". Powers his break-test workflow: he plays + breaks, I
    // replay + run __replayDiff() to see exactly what doesn't match.
    w.__replayDiff = async (originalRunId) => {
      const targetUser = username || 'DevPeanut';
      try {
        // 1. Fetch the original run (by id, OR most-recent if no id passed)
        let url: string;
        if (originalRunId) {
          url = `${SUPABASE_URL}/rest/v1/bug_reports?select=*&id=eq.${encodeURIComponent(originalRunId)}&apikey=${SUPABASE_KEY}`;
        } else {
          const reporterFilter = `reporter=eq.${encodeURIComponent('live-play:' + targetUser)}`;
          url = `${SUPABASE_URL}/rest/v1/bug_reports?select=*&order=created_at.desc&limit=1&${reporterFilter}&category=eq.live_play&apikey=${SUPABASE_KEY}`;
        }
        const res = await fetch(url);
        if (!res.ok) { console.warn('[__replayDiff]', res.status, await res.text()); return null; }
        const rows = (await res.json()) as SupaRow[];
        if (!Array.isArray(rows) || rows.length === 0) { console.warn('[__replayDiff] no run found'); return null; }
        const data = _parseRow(rows[0]);
        if (!data) { console.warn('[__replayDiff] parse failed'); return null; }

        // 2. Pull replica's current stats
        const p = (window as Window & { __getPlayer?: () => Record<string, number | undefined> | null }).__getPlayer?.();
        if (!p) { console.warn('[__replayDiff] no live player — replay not running?'); return null; }

        // 3. Build the comparison table. Field names match between RunPayload's
        // "final*" fields and the live player object.
        type DataAny = Record<string, number | undefined>;
        const d = data as unknown as DataAny;
        const diff: Record<string, { real: number; replica: number; deltaPct: number }> = {};
        const cmp = (label: string, realVal: number | undefined, replicaVal: number | undefined): void => {
          const r = realVal ?? 0, x = replicaVal ?? 0;
          const dpct = r === 0 ? (x === 0 ? 0 : 999) : Math.round(((x - r) / Math.abs(r)) * 100);
          diff[label] = { real: Math.round(r * 100) / 100, replica: Math.round(x * 100) / 100, deltaPct: dpct };
        };
        cmp('damageBonus',       d.finalDamagePct,         p.damageBonus);
        cmp('attackSpeedBonus',  d.finalAttackSpeedPct,    p.attackSpeedBonus);
        cmp('critChance',        d.finalCritChancePct,     p.critChance);
        cmp('critDamage',        d.finalCritDamagePct,     p.critDamage);
        cmp('lifesteal',         d.finalLifestealPct,      p.lifesteal);
        cmp('armor',             d.finalArmor,             p.armor);
        cmp('shield',            d.finalShield,            p.shield);
        cmp('maxHp',             d.finalMaxHP,             p.maxHp);
        cmp('projectileCount',   d.finalProjectileCount,   p.projectileCountBonus);
        cmp('projectileBounce',  d.finalProjectileBounce,  p.projectileBounceBonus);
        cmp('knockback',         d.finalKnockbackPct,      p.knockbackBonus);
        cmp('pickupRange',       d.finalPickupRangePct,    p.pickupRange);
        cmp('phaseChance',       d.finalPhaseChancePct,    p.contactPhaseChance ? p.contactPhaseChance * 100 : 0);
        cmp('xpMultiplier',      d.finalXpMultiplier,      p.xpMultiplier);
        cmp('goldMultiplier',    d.finalGoldMultiplier,    p.goldMultiplier);
        cmp('hpRegen',           d.finalHpRegen,           p.hpRegen);
        cmp('shieldRegen',       d.finalShieldRegen,       p.shieldRegen);
        cmp('luck',              d.finalLuckPct,           p.luck);
        cmp('evasion',           d.finalEvasionPct,        p.evasion);
        cmp('thorns',            d.finalThornsPct,         p.thorns);
        cmp('moveSpeed',         d.finalRawSpeed,          p.speed);

        // 4. Sort by abs delta% desc so biggest divergences float to top
        const sortedEntries = Object.entries(diff).sort((a, b) => Math.abs(b[1].deltaPct) - Math.abs(a[1].deltaPct));
        const sortedDiff = Object.fromEntries(sortedEntries);
        console.log(`[__replayDiff] real run ${rows[0].id?.slice(0, 8)} (${d.gameVersion}) vs live replica:`);
        console.table(sortedDiff);
        return sortedDiff;
      } catch (e) {
        console.warn('[__replayDiff] threw', e);
        return null;
      }
    };

    w.__replayLastRun = async (opts) => {
      const targetUser = username || 'DevPeanut';
      try {
        const reporterFilter = `reporter=eq.${encodeURIComponent('live-play:' + targetUser)}`;
        const url = `${SUPABASE_URL}/rest/v1/bug_reports?select=*&order=created_at.desc&limit=1&${reporterFilter}&category=eq.live_play&apikey=${SUPABASE_KEY}`;
        const res = await fetch(url);
        const rows = (await res.json()) as SupaRow[];
        if (!Array.isArray(rows) || rows.length === 0) {
          console.warn('[__replayLastRun] no live_play runs found for', targetUser);
          return false;
        }
        return await _spawnFromRow(rows[0], opts);
      } catch (e) {
        console.warn('[__replayLastRun] fetch/parse threw', e);
        return false;
      }
    };

    w.__returnToMenu = handleBackToMenu;
    w.__getGameScreen = () => screen;
    w.__getGamePhase = () => screen; // alias
    // Lazy import — debugState only needed when an external orchestrator overrides speed
    w.__setTimeScale = (v: number) => {
      void import('@/game/debugState').then(m => m.setDebugTimeScale(v));
    };
    w.__getTimeScale = () => {
      // BETA 17.85 — Was reading __botCurrentRun()?.timeScale which is the
      // LAST RUN's time scale, NOT the live debug value. After a debug-time-
      // scale change via __setTimeScale or the F9 debug panel, __getTimeScale
      // would lie by returning the prior run's value (or 1 if no run). Now
      // reads the localStorage source of truth that setDebugTimeScale
      // persists to. Caught when investigating "why is the bot running at
      // 5× when getTimeScale says 1×" — Dylan: "see how fast the fucking
      // enemies are moving... unless there is insane player speed running
      // buffs. you are plying this at a higher speed."
      try {
        const raw = localStorage.getItem('goopzone_time_scale');
        const v = raw != null ? parseFloat(raw) : 1.0;
        return Number.isFinite(v) && v > 0 ? v : 1.0;
      } catch { return 1.0; }
    };
    return () => { delete w.__startGame; delete w.__startWithBuild; delete w.__replayLastRun; delete w.__listRecentRuns; delete w.__replayRun; delete w.__replayDiff; delete w.__returnToMenu; delete w.__getGameScreen; delete w.__getGamePhase; delete w.__setTimeScale; delete w.__getTimeScale; };
  }, [screen, handleStartGame, handleBackToMenu, username]);

  // Called when a run ends (game over or win)
  const handleRunEnd = useCallback(async (stats: RunStats) => {
    // ── Update lifetime stats & check quest unlocks ──────────────
    const runResults: RunResults = {
      killsThisRun:         stats.kills,
      goldEarnedThisRun:    0,   // TODO: pass from engine (player.gold)
      goopEarnedThisRun:    Math.max(10, Math.floor(stats.kills * 2)),  // 2 GOOP per kill
      waveReached:          stats.floor,
      levelReached:         stats.level,
      survivalMinutes:      stats.timeSurvived / 60,
      bossesKilledThisRun:  0,   // TODO: pass from engine
      died:                 true,
      beatBossAndPortaled:  false,
      chestsOpened:         stats.chestsOpened,
      healingDone:          0,
      highestArmor:         0,
      highestSpeed:         0,
      thornsDamage:         0,
      damageTaken:          0,
      enemiesPoisoned:      0,
      longestKillStreak:    0,
      distanceTraveled:     0,
      criticalHits:         0,
      lowHpSeconds:         0,
      damageDealt:          stats.damageDealt,
    };

    // goopSystem mutates state in-place; spread triggers React re-render
    const newlyCompleted = updateLifetimeStats(goopState, runResults);
    saveGoopState(goopState);

    // Sync character unlock state to loadoutSelection + menu renderer
    // BETA 19.35 — pass updated lifetimeStats so any shared weapon whose
    // quest condition just became true gets unlocked immediately after
    // the run (no need to refresh the page).
    const charIds = goopState.unlockedCharacterIds ?? new Set<string>();
    setUnlockedCharacterIds(charIds, goopState.lifetimeStats);
    setMenuLiveData(goopState.lifetimeStats, goopState.completedQuests);

    setGoopState({ ...goopState });

    // Show post-run unlock screen if anything new was unlocked, else go straight to menu
    if (newlyCompleted.length > 0) {
      setPostRunUnlocks(newlyCompleted);
      setScreen('post_run_unlocks');
    } else {
      setScreen('menu');
    }

    // ── Submit leaderboard score ──────────────────────────────────
    if (!loggedIn) return;
    try {
      await submitScore({
        kills: stats.kills,
        floor_reached: stats.floor,
        time_survived: stats.timeSurvived,
        character_id: stats.characterId,
        username,   // BETA 21.20 — dev-session fallback so tester-code players rank too
      });
    } catch (e) {
      console.error('[Leaderboard] Score submit failed:', e);
    }
  }, [loggedIn, goopState, username]);

  // Settings from title/quests screens — swap screen
  const handleOpenSettings = useCallback(() => {
    setPrevScreen(screen);
    setScreen('settings');
  }, [screen]);

  const handleCloseSettings = useCallback(() => {
    setScreen(prevScreen);
  }, [prevScreen]);

  // Settings from in-game pause menu — overlay only, keeps GameCanvas alive
  const handleOpenSettingsOverlay = useCallback(() => {
    setShowSettingsOverlay(true);
  }, []);

  const handleCloseSettingsOverlay = useCallback(() => {
    setShowSettingsOverlay(false);
  }, []);

  const handleOpenQuests = useCallback(() => {
    setPrevScreen(screen);
    setScreen('quests');
  }, [screen]);

  const handleCloseQuests = useCallback(() => {
    setScreen(prevScreen);
  }, [prevScreen]);

  // BETA 15.83 — Per Dylan: "i cant mute or do volume controlls or master
  // volume in settings or sfx from home scren setings." Root cause: this
  // callback saved settings to state + localStorage but NEVER applied them
  // to the audio engine. GameCanvas applied them on mount, but home-screen
  // changes (before clicking play) were silent. Now the audio engine is
  // updated immediately on every slider change.
  // Note: musicVolume controls the BGM track now (15.81 disabled the
  // procedural drone, BGM is "the music" from a user's perspective).
  const handleSettingsChange = useCallback((settings: AudioSettings) => {
    setUserSettings(prev => {
      const merged: UserSettings = { ...prev, ...settings };
      saveUserSettings(merged, username || null);
      return merged;
    });
    setMasterVolume(settings.masterVolume);
    setMusicVolume(settings.musicVolume);   // procedural musicGain (still wired for completeness)
    setBgmVolume(settings.musicVolume);     // BGM = "music" from Dylan's POV — same slider drives both
    setSFXVolume(settings.sfxVolume);
  }, [username]);

  // BETA 16.56 — persist HUD scale + any non-audio fields whenever userSettings changes.
  // (Audio writes also happen in handleSettingsChange above, so this is the
  //  catch-all for fields like hudScale + future graphics/control toggles.)
  // BETA 16.99 — Per Dylan: "whatver my settings are i change it to need to
  // save forever until i change them again. that is why we have sign in's.
  // i should not have to be redoing this each round." Now: save to BOTH
  // anonymous AND user-keyed buckets when signed in. On a fresh page load,
  // useState(loadUserSettings(null)) reads the anonymous bucket BEFORE
  // login resolves — if the anonymous bucket has the user's last settings,
  // they apply immediately instead of flashing default 'auto' for the
  // 1-3s before login finishes loading the user-keyed record.
  useEffect(() => {
    saveUserSettings(userSettings, username || null);
    if (username) {
      // Mirror to anonymous bucket as a "last-known good" fallback for
      // pre-login boot rendering.
      saveUserSettings(userSettings, null);
    }
  }, [userSettings, username]);

  // BETA 16.59 — push UserSettings.graphics fields into the live
  // graphicsSettings module on every change. Mutations from inside
  // SettingsScreen go directly to graphicsSettings, but on load and on
  // login-swap we need to rehydrate from the persisted record.
  useEffect(() => {
    applyGraphicsSettings({
      particleDensity: userSettings.particleDensity,
      // BETA 16.64 — preset takes precedence; legacy resolutionScale
      // is only used as fallback for users with old saved records.
      resolutionPreset: userSettings.resolutionPreset,
      resolutionScale: userSettings.resolutionScale,
      screenShakeEnabled: userSettings.screenShakeEnabled,
      glowEffectsEnabled: userSettings.glowEffectsEnabled,
      screenFlashEnabled: userSettings.screenFlashEnabled,
      dynamicLightingEnabled: userSettings.dynamicLightingEnabled,
    });
  }, [userSettings]);

  // BETA 16.59 — Settings Screen's onGraphicsChange ping. After the user
  // toggles particle density / shake / flash / glow / resolution, sync
  // the live values back into userSettings so saveUserSettings persists.
  const handleGraphicsChange = useCallback(() => {
    setUserSettings(prev => {
      const next: UserSettings = {
        ...prev,
        particleDensity: getParticleDensity(),
        resolutionPreset: getResolutionPreset(),
        resolutionScale: getResolutionScale(),
        screenShakeEnabled: getScreenShakeEnabled(),
        glowEffectsEnabled: getGlowEffectsEnabled(),
        screenFlashEnabled: getScreenFlashEnabled(),
        dynamicLightingEnabled: getDynamicLightingEnabled(),
      };
      saveUserSettings(next, username || null);
      return next;
    });
  }, [username]);

  // BETA 16.56 — when login resolves, swap to the user-keyed settings
  // record (or seed it from current anonymous settings). This is the
  // "settings follow the account" promise.
  useEffect(() => {
    if (!loggedIn || !username) return;
    const userKeyed = loadUserSettings(username);
    // BETA 16.99 — Login-swap MERGE (was overwrite). Preserve any
    // in-memory settings the user changed before login resolved by
    // merging on top of the user-keyed record. If user-keyed has a
    // value for a field, it wins; if it doesn't, the in-memory value
    // (typically anonymous-bucket from initial load) wins. Avoids
    // losing changes made in the brief window between page-load and
    // session-restore.
    setUserSettings(prev => {
      const merged: UserSettings = { ...prev, ...userKeyed };
      saveUserSettings(merged, username);
      // Mirror to anonymous bucket so next pre-login boot picks it up.
      saveUserSettings(merged, null);
      return merged;
    });
  }, [loggedIn, username]);

  // BETA 13.71: Weapon Visual Test Lab — accessible from start menu without going through Play.
  const handleOpenLab = useCallback(() => { setScreen('weapon_lab'); }, []);
  // BETA 15.33 — Room Lab handler (mirrors weapon_lab pattern)
  const handleOpenRoomLab = useCallback(() => { setScreen('room_lab'); }, []);
  const handleCloseRoomLab = useCallback(() => { setScreen('menu'); }, []);
  const handleCloseLab = useCallback(() => { setScreen('menu'); }, []);
  // BETA 22.42 — START opens the unified tabbed loadout screen (chalkboard-framed).
  // BETA 22.95 — LoadoutScreen deleted (dead old-perk/item art screen; ModBench is the real one)
  // BETA 22.50 — MOD BENCH opens the new Sorry Boss bench (Dylan's mockup layout).
  const handleOpenModBench = useCallback(() => { setScreen('modbench'); }, []);

  return (
    <>
    {/* BETA 16.66 — MobileGuards mounted at the absolute top of the JSX
        so it covers EVERYTHING (start menu, game, settings, leaderboard,
        chest spin, even the LET'S RIDE overlay). Renders the
        portrait-rotation full-screen warning + Add-to-Home-Screen hint
        for iPhone Safari + auto-fullscreen attempts on first touch. No-op
        on desktop / non-touch devices. */}
    <MobileGuards />
    {/* BETA 22.49 — Sorry Boss boot intro: BLAME CULTURE studio card → title video
        → freeze-frame + embers → menu. Covers everything (zIndex 10050) until done. */}
    {showIntro && <IntroSequence onComplete={dismissIntro} />}
    <AnimatePresence mode="wait">
      {screen === 'menu' && (
        <div key="menu" style={{ position: 'fixed', inset: 0 }}>
          <TitleScreen
            onStartGame={handleStartGame}
            onSettings={handleOpenSettings}
            onQuests={handleOpenQuests}
            onLoadout={handleOpenModBench}
            onSignOut={loggedIn ? handleSignOut : undefined}
            signedIn={loggedIn}
            onRequestSignIn={handleRequestSignIn}
            username={username}
            inputLocked={showIntro}
          />
          {/* BETA 22.52 — sign-in as an OVERLAY on the menu, raised by START
              RUN (or the SIGN IN plate) instead of walling the boot. Closed
              beta rules unchanged (BETA 16.85): no guest play — onSkip is not
              passed; the waitlist tab still lets new people request access. */}
          {showAuthOverlay && (
            <AuthScreen
              overlay
              onClose={() => { pendingStartRef.current = null; setShowAuthOverlay(false); }}
              onSuccess={handleAuthSuccess}
              onDevCode={handleDevCode}
            />
          )}
          {/* DEV menu toggle — Dylan: "one Dev button ... everything else lives
              inside it, its getting messy." Only this + the version badge show on
              the main screen; the rest of the dev tools collapse behind it.
              BETA 22.51 — hidden entirely unless the URL carries ?dev=1. */}
          {_devToolsRequested && (!isTouchDevice() || isDevUser(username)) && (
          <button
            id="gpdev-toggle" data-gp-focus="DEV"
            onClick={() => setDevMenuOpen(o => !o)}
            title="Toggle the dev tools menu"
            style={{
              // BETA 22.50 — the ● BETA badge moved into StartMenu's bottom-right, so
              // DEV takes the old badge spot at stage bottom-left. Sits BELOW the
              // Discord/News row (their design-space bottom is y=862; stage is 900 tall),
              // so no overlap. The collapsible tool stack lives at bottoms 52+.
              position: 'fixed', bottom: _stageBottom(6), left: _stageLeft(12), zIndex: 42,
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '0.7rem', letterSpacing: '0.12em',
              padding: '7px 14px', cursor: 'pointer', borderRadius: 2,
              background: devMenuOpen ? 'rgba(30,40,60,0.95)' : 'rgba(20,25,35,0.92)',
              color: 'rgba(160,200,255,0.95)',
              border: '1px solid rgba(120,170,255,0.5)',
              boxShadow: '0 0 8px rgba(120,170,255,0.2)',
            }}
          >
            🛠 DEV {devMenuOpen ? '▾' : '▸'}
          </button>
          )}

          {devMenuOpen && (<>
          {/* Bot mode toggle — bottom-left corner, visible before game starts.
              BETA 16.67 — Hidden on touch devices. Per Dylan playing on iPhone:
              "i can not see the yellow confirm button" — these dev/admin
              floating buttons (bot mode, weapon lab, room lab, CSV download)
              were overlapping the chalkboard menu on the small iPhone Air
              landscape viewport (852×393 with 698-wide chalkboard centered),
              hiding the CONFIRM button on the right panel and crowding the
              left margin. Players on phones don't need any of these.
              BETA 20.54 — DEV USERS still get them on ANY device, including
              touchscreen handhelds (the ROG Ally X trips isTouchDevice via
              navigator.maxTouchPoints > 0), so the gate now also allows
              isDevUser(username). */}
          {(!isTouchDevice() || isDevUser(username)) && (
          <button
            id="gpdev-botmode" data-gp-focus="BOT MODE"
            onClick={() => setBotMode(m => !m)}
            style={{
              position: 'fixed', bottom: _stageBottom(52), left: _stageLeft(12), zIndex: 40,
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '0.7rem', letterSpacing: '0.12em',
              padding: '7px 14px', cursor: 'pointer', borderRadius: 2,
              background: botMode ? 'rgba(255,60,0,0.22)' : 'rgba(20,30,20,0.88)',
              color: botMode ? '#ff6a00' : 'rgba(180,210,160,0.7)',
              border: botMode ? '1px solid rgba(255,100,0,0.6)' : '1px solid rgba(150,200,120,0.3)',
              boxShadow: botMode ? '0 0 12px rgba(255,80,0,0.4)' : 'none',
              transition: 'all 0.2s',
            }}
          >
            🤖 BOT MODE {botMode ? 'ON — loops forever' : 'OFF'}
          </button>
          )}

          {/* BETA 13.71: Weapon Visual Test Lab — frozen player, all items toggleable, CSV-export notes.
              BETA 16.67 — Hidden on touch (dev tool). */}
          {(!isTouchDevice() || isDevUser(username)) && (
          <button
            id="gpdev-weaponlab" data-gp-focus="WEAPON LAB"
            onClick={handleOpenLab}
            title="Open Weapon Lab — test weapon visuals + take CSV-export notes without playing a real run"
            style={{
              position: 'fixed', bottom: _stageBottom(92), left: _stageLeft(12), zIndex: 40,
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '0.7rem', letterSpacing: '0.12em',
              padding: '7px 14px', cursor: 'pointer', borderRadius: 2,
              background: 'rgba(20,30,40,0.88)',
              color: 'rgba(127,220,255,0.9)',
              border: '1px solid rgba(80,200,255,0.5)',
              boxShadow: '0 0 8px rgba(80,200,255,0.18)',
            }}
          >
            🔬 WEAPON LAB
          </button>
          )}

          {/* BETA 21.62 — Balance Dashboard: dev tool to see + edit every item/perk/shrine
              number live (saved to Supabase). Dev-only. */}
          {(!isTouchDevice() || isDevUser(username)) && (
          <button
            id="gpdev-balance" data-gp-focus="BALANCE"
            onClick={() => { window.location.href = '/balance'; }}
            title="Open the Balance Dashboard — see + edit every item/perk/shrine number live"
            style={{
              // BETA 21.62b — 172 collided with the Replays launcher (zIndex 9000, same
              // spot) which hid this button entirely. 252 is clear of Replays (172) + its
              // expanded panel (212).
              position: 'fixed', bottom: _stageBottom(252), left: _stageLeft(12), zIndex: 41,
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '0.7rem', letterSpacing: '0.12em',
              padding: '7px 14px', cursor: 'pointer', borderRadius: 2,
              background: 'rgba(40,30,10,0.9)',
              color: 'rgba(255,215,0,0.9)',
              border: '1px solid rgba(255,200,0,0.5)',
              boxShadow: '0 0 8px rgba(255,200,0,0.18)',
            }}
          >
            ⚖️ BALANCE
          </button>
          )}

          {/* BETA 22.29 — Peanut Voices tab: go through every voice clip, trim/extend,
              approve ✅ Peanut / ❌ friend, delete. Editing works on the local dev server. */}
          {(!isTouchDevice() || isDevUser(username)) && (
          <button
            id="gpdev-voices" data-gp-focus="VOICES"
            onClick={() => { window.location.href = '/voices'; }}
            title="Open the Peanut Voices library — listen, trim, approve + delete clips"
            style={{
              // 332 = next free slot above COSMETICS (292); 172/212 reserved for Replays.
              // zIndex 9001 (above the Replays launcher 9000) so it's never covered.
              position: 'fixed', bottom: _stageBottom(332), left: _stageLeft(12), zIndex: 9001,
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '0.7rem', letterSpacing: '0.12em',
              padding: '7px 14px', cursor: 'pointer', borderRadius: 2,
              background: 'rgba(40,30,10,0.9)',
              color: 'rgba(255,225,150,0.95)',
              border: '1px solid rgba(255,200,0,0.5)',
              boxShadow: '0 0 8px rgba(255,200,0,0.18)',
            }}
          >
            🥜 VOICES
          </button>
          )}

          {/* Mod Bench launcher — socket Starforge weapon mods (attachments) onto guns. */}
          {(!isTouchDevice() || isDevUser(username)) && (
          <button
            id="gpdev-mods" data-gp-focus="MODS"
            onClick={() => { window.location.href = '/mods'; }}
            title="Open the Mod Bench — socket Starforge mods onto your weapons"
            style={{
              position: 'fixed', bottom: _stageBottom(372), left: _stageLeft(12), zIndex: 41,
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '0.7rem', letterSpacing: '0.12em',
              padding: '7px 14px', cursor: 'pointer', borderRadius: 2,
              background: 'rgba(20,20,45,0.9)',
              color: 'rgba(150,200,255,0.95)',
              border: '1px solid rgba(120,160,255,0.5)',
              boxShadow: '0 0 8px rgba(120,160,255,0.18)',
            }}
          >
            🔧 MOD BENCH
          </button>
          )}

          {/* BETA 22.95 — dev LOADOUT button deleted with LoadoutScreen (old perk/item art) */}

          {/* BETA 15.33 — Room Lab: pick a layout shape, see real walls + doorways
              + collision check live. Built to debug the bot-stuck-at-doorway issue.
              BETA 16.67 — Hidden on touch (dev tool). */}
          {(!isTouchDevice() || isDevUser(username)) && (
          <button
            id="gpdev-roomlab" data-gp-focus="ROOM LAB"
            onClick={handleOpenRoomLab}
            title="Open Room Lab — visualize room collision geometry + test player movement at doorways"
            style={{
              position: 'fixed', bottom: _stageBottom(132), left: _stageLeft(12), zIndex: 40,
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '0.7rem', letterSpacing: '0.12em',
              padding: '7px 14px', cursor: 'pointer', borderRadius: 2,
              background: 'rgba(30,20,40,0.88)',
              color: 'rgba(220,127,255,0.9)',
              border: '1px solid rgba(200,120,255,0.5)',
              boxShadow: '0 0 8px rgba(200,120,255,0.18)',
            }}
          >
            🏛️ ROOM LAB
          </button>
          )}

          {/* Bot CSV download button — shows whenever there are saved runs.
              BETA 16.67 — Hidden on touch (dev tool). */}
          {savedBotRuns > 0 && (!isTouchDevice() || isDevUser(username)) && (
            <div style={{ position: 'fixed', bottom: _stageBottom(52), left: _stageLeft(200), zIndex: 40, display: 'flex', gap: 6, alignItems: 'center' }}>
              <button
                id="gpdev-botcsv" data-gp-focus="BOT CSV"
                onClick={() => downloadFullCSV()}
                style={{
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: '0.7rem', letterSpacing: '0.12em',
                  padding: '7px 14px', cursor: 'pointer', borderRadius: 2,
                  background: 'rgba(0,40,20,0.92)',
                  color: '#00ff88',
                  border: '1px solid rgba(0,255,100,0.5)',
                  boxShadow: '0 0 8px rgba(0,255,100,0.2)',
                }}
              >
                📊 BOT CSV ({savedBotRuns} runs)
              </button>
              <button
                id="gpdev-botclear" data-gp-focus="CLEAR CSV"
                onClick={() => { clearBotRunData(); setSavedBotRuns(0); }}
                title="Clear saved bot run data"
                style={{
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: '0.65rem',
                  padding: '7px 10px', cursor: 'pointer', borderRadius: 2,
                  background: 'rgba(40,0,0,0.88)',
                  color: 'rgba(255,80,80,0.6)',
                  border: '1px solid rgba(200,50,50,0.3)',
                }}
              >
                🗑
              </button>
            </div>
          )}
          </>)}

          {/* BETA 22.51 — the whole floating top-right bar is GONE (Dylan video
              feedback): the 🏆 LEADERBOARD button was peanut-era UI, viewport-fixed
              so it floated off the 16:9 stage on resize, sat on top of the co-op
              lobby's ✕, and opened the old green LeaderboardScreen. Future direction
              (his idea, not built yet): leaderboard DATA living always-visible on
              the menu. SIGN OUT + username now live INSIDE StartMenu (player plate
              shows the name; SIGN OUT is a stage-anchored button in the Discord/News
              row, passed via onSignOut only when signed in). */}
        </div>
      )}

      {screen === 'game' && selectedChar && (
        // BETA 19.43 — key includes selectedChar.id so React remounts
        // GameCanvas when character changes mid-session. Was static
        // `key="game"` — calling __startGame('killa') after the game
        // was already running as Chad kept the same GameCanvas mounted,
        // engine init never re-fired with the new character, weapon
        // stayed chad_aura even though selectedChar was killa. Caught
        // live via chrome-devtools MCP debugging.
        <div key={`game-${selectedChar.id}`} className="fixed inset-0">
          <GameCanvas
            character={selectedChar}
            onBack={handleBackToMenu}
            onOpenSettings={handleOpenSettingsOverlay}
            onRunEnd={botMode ? undefined : handleRunEnd}
            audioSettings={audioSettings}
            hudScale={hudScale}
            username={username}
            goopState={goopState}
            botMode={botMode}
          />
          {showSettingsOverlay && (
            <div className="fixed inset-0 z-50">
              <SettingsScreen
                onClose={handleCloseSettingsOverlay}
                initialSettings={audioSettings}
                onSettingsChange={handleSettingsChange}
                hudScale={hudScale}
                onHudScaleChange={setHudScale}
                onGraphicsChange={handleGraphicsChange}
              />
            </div>
          )}
        </div>
      )}

      {screen === 'settings' && (
        <SettingsScreen
          key="settings"
          onClose={handleCloseSettings}
          initialSettings={audioSettings}
          onSettingsChange={handleSettingsChange}
          hudScale={hudScale}
          onHudScaleChange={setHudScale}
          onGraphicsChange={handleGraphicsChange}
        />
      )}

      {screen === 'quests' && (
        <QuestsScreen
          key="quests"
          onClose={handleCloseQuests}
          goopState={goopState}
        />
      )}

      {screen === 'post_run_unlocks' && (
        <PostRunUnlockScreen
          key="post_run_unlocks"
          questIds={postRunUnlocks}
          goopState={goopState}
          onContinue={() => setScreen('menu')}
        />
      )}

      {screen === 'weapon_lab' && (
        <WeaponLabScreen
          key="weapon_lab"
          onBack={handleCloseLab}
          audioSettings={audioSettings}
          username={username}
        />
      )}

      {/* BETA 15.33 — Room Lab. Standalone tester for room collision geometry. */}
      {screen === 'room_lab' && (
        <RoomLabScreen
          key="room_lab"
          onBack={handleCloseRoomLab}
        />
      )}

      {/* BETA 22.42 — Unified tabbed loadout screen (chalkboard-framed) opened by START. */}
      {screen === 'modbench' && (
        <ModBench onClose={() => setScreen('menu')} />
      )}


    </AnimatePresence>

    {/* Dev bug-report panel — outside AnimatePresence so it persists across all screens */}
    <DevPanel username={username} currentScreen={screen} />
    {devMenuOpen && <ReplayLauncherPanel username={username} currentScreen={screen} />}
    </>
  );
}
